import { useState, useRef } from 'react';
import { useReveal } from '../../../hooks/useReveal';

export default function ContactSection() {
  const { ref: headRef, isVisible: headVisible } = useReveal<HTMLDivElement>(0.2);
  const { ref: cardsRef, isVisible: cardsVisible } = useReveal<HTMLDivElement>(0.1);
  const { ref: formRef, isVisible: formVisible } = useReveal<HTMLDivElement>(0.1);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formElRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formElRef.current) return;
    setIsSubmitting(true);
    const data = new URLSearchParams(new FormData(formElRef.current) as unknown as URLSearchParams);
    try {
      await fetch('https://readdy.ai/api/form/d6tm7hcj8d20juc90670', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
    } catch (_) {
      // silent
    } finally {
      setIsSubmitting(false);
      setFormSubmitted(true);
    }
  };

  const contactCards = [
    {
      icon: 'ri-phone-line',
      title: 'Call Us',
      value: '(615) 602-9594',
      href: 'tel:+16156029594',
      isGold: true,
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Us',
      value: 'Parker.Pierce@IronCladMarketingCo.com',
      href: 'mailto:Parker.Pierce@IronCladMarketingCo.com',
      isGold: true,
    },
    {
      icon: 'ri-chat-1-line',
      title: 'Send a Message',
      value: 'Use the form below',
      href: '',
      isGold: false,
    },
  ];

  return (
    <section
      id="contact"
      className="relative py-20 md:py-28 px-4 sm:px-6 lg:px-12 overflow-hidden"
      style={{ background: '#000' }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          backgroundImage: 'url(https://static.readdy.ai/image/ad76438f27b7c76a25e10aafd906f29a/e4f6ee985377cba9b679ed9e1bdbb64a.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.9,
        }}
      />
      <div className="absolute inset-0 bg-black/30" />

      <div className="relative max-w-3xl mx-auto">

        {/* Heading — centered */}
        <div
          ref={headRef}
          className="text-center mb-12"
          style={{
            opacity: headVisible ? 1 : 0,
            transform: headVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-5"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Let&apos;s Start a <span className="text-[#D4AF37]">Conversation</span>
          </h2>
          <p
            className="text-white/60 text-sm md:text-base leading-relaxed mb-6"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Reach out to our team to check your market availability.
          </p>
          {/* Gold divider */}
          <div className="flex items-center justify-center">
            <div className="h-px w-32 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          </div>
        </div>

        {/* Contact cards */}
        <div
          ref={cardsRef}
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8"
        >
          {contactCards.map((card, i) => (
            <div
              key={i}
              className="border border-white/10 rounded-lg p-5 md:p-6 flex flex-col items-start gap-3"
              style={{
                background: 'rgba(15,15,15,0.75)',
                backdropFilter: 'blur(6px)',
                opacity: cardsVisible ? 1 : 0,
                transform: cardsVisible ? 'translateY(0)' : 'translateY(28px)',
                transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`,
              }}
            >
              <div className="w-10 h-10 flex items-center justify-center border border-white/15 rounded-md">
                <i className={`${card.icon} text-lg text-[#D4AF37]`} />
              </div>
              <h3
                className="text-sm font-bold text-white"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {card.title}
              </h3>
              {card.href ? (
                <a
                  href={card.href}
                  className="text-[11px] text-[#D4AF37] hover:brightness-110 transition-all cursor-pointer break-all"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {card.value}
                </a>
              ) : (
                <span
                  className="text-sm text-white/55"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {card.value}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Contact form */}
        <div
          ref={formRef}
          className="border border-white/10 rounded-lg p-8 md:p-10"
          style={{
            background: 'rgba(12,12,12,0.82)',
            backdropFilter: 'blur(8px)',
            opacity: formVisible ? 1 : 0,
            transform: formVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          {formSubmitted ? (
            <div className="flex flex-col items-center py-14 text-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center border border-[#D4AF37]/40 rounded-full">
                <i className="ri-check-line text-2xl text-[#D4AF37]" />
              </div>
              <h3
                className="text-2xl font-bold text-white mt-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Message Received
              </h3>
              <p
                className="text-white/50 text-sm max-w-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                We&apos;ll be in touch within 24 hours to schedule your free strategy call.
              </p>
            </div>
          ) : (
            <>
              <div className="text-center mb-7">
                <h3
                  className="text-xl font-bold text-white mb-2"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Send Us a Message
                </h3>
                <p
                  className="text-white/45 text-sm"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  Fill out the form below and we&apos;ll be in touch within 24 hours.
                </p>
              </div>

              <form
                ref={formElRef}
                data-readdy-form
                className="flex flex-col gap-5"
                onSubmit={handleSubmit}
              >
                {/* Row 1 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label
                      className="block text-white/40 text-[10px] mb-2 uppercase tracking-widest"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      required
                      className="w-full px-4 py-3 bg-white/[0.05] border border-white/10 rounded-md text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/40 transition-colors"
                      placeholder="Jane Smith"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-white/40 text-[10px] mb-2 uppercase tracking-widest"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-white/[0.05] border border-white/10 rounded-md text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/40 transition-colors"
                      placeholder="jane@smithlawfirm.com"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                </div>

                {/* Row 2 */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label
                      className="block text-white/40 text-[10px] mb-2 uppercase tracking-widest"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full px-4 py-3 bg-white/[0.05] border border-white/10 rounded-md text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/40 transition-colors"
                      placeholder="(555) 123-4567"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-white/40 text-[10px] mb-2 uppercase tracking-widest"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Law Firm Name
                    </label>
                    <input
                      type="text"
                      name="firm_name"
                      required
                      className="w-full px-4 py-3 bg-white/[0.05] border border-white/10 rounded-md text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/40 transition-colors"
                      placeholder="Smith &amp; Associates"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    />
                  </div>
                </div>

                {/* Row 3 — full width textarea */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label
                      className="block text-white/40 text-[10px] uppercase tracking-widest"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      Tell Us About Your Firm (Optional)
                    </label>
                    <span className="text-[10px] text-white/25" style={{ fontFamily: 'Inter, sans-serif' }}>
                      {charCount}/500
                    </span>
                  </div>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white/[0.05] border border-white/10 rounded-md text-white text-sm placeholder-white/20 focus:outline-none focus:border-[#D4AF37]/40 transition-colors resize-none"
                    placeholder="Practice areas, locations, or growth goals..."
                    onChange={e => setCharCount(e.target.value.length)}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-[#D4AF37] text-black text-xs font-bold rounded-md hover:bg-[#e8c547] transition-all duration-300 whitespace-nowrap cursor-pointer tracking-widest uppercase disabled:opacity-60"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {/* No spam note */}
                <p
                  className="text-center text-white/35 text-xs flex items-center justify-center gap-1.5"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  <i className="ri-lock-line text-xs" />
                  No spam. No obligation. Just a real conversation about growth.
                </p>
              </form>
            </>
          )}
        </div>

        {/* Bottom trust badge */}
        <p
          className="text-center text-white/40 text-xs mt-8 tracking-wide"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          Response within 24 hours &bull; Exclusive leads for personal injury firms
        </p>

      </div>

      {/* Footer */}
      <div className="relative max-w-7xl mx-auto mt-16 md:mt-24 pt-12 border-t border-white/[0.07]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-10 mb-12">
          <div className="col-span-2 md:col-span-1">
            <img
              src="https://static.readdy.ai/image/ad76438f27b7c76a25e10aafd906f29a/1f866004502f08d41232135bfc42dc55.png"
              alt="IronClad Leads"
              className="h-10 w-auto mb-5"
            />
            <p
              className="text-white/35 text-sm leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Exclusive Google Ads lead generation for personal injury law firms. One firm per market. Real results.
            </p>
          </div>
          {[
            {
              title: 'Services',
              links: ['Google Ads Management', 'Exclusive Lead Gen', 'Landing Page Design', 'Call Tracking'],
            },
            {
              title: 'Company',
              links: ['About IronClad', 'How It Works', 'Why Choose Us', 'Contact'],
            },
            {
              title: 'Legal',
              links: ['Privacy Policy', 'Terms of Service', 'Cookie Policy'],
            },
          ].map((col, i) => (
            <div key={i}>
              <h4
                className="text-white text-xs font-semibold mb-5 uppercase tracking-widest"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                {col.title}
              </h4>
              <ul className="space-y-3">
                {col.links.map(link => (
                  <li key={link}>
                    <a
                      href="#"
                      rel="nofollow"
                      className="text-white/35 text-sm hover:text-[#D4AF37] transition-colors cursor-pointer"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-white/[0.06]">
          <p
            className="text-white/25 text-xs"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            &copy; 2026 IronClad Leads. All rights reserved.
          </p>
          <div className="flex items-center gap-3">
            {[
              { icon: 'ri-linkedin-fill', href: '#' },
              { icon: 'ri-twitter-x-fill', href: '#' },
              { icon: 'ri-facebook-fill', href: '#' },
            ].map((social, i) => (
              <a
                key={i}
                href={social.href}
                rel="nofollow"
                className="w-9 h-9 flex items-center justify-center border border-white/10 rounded-md text-white/35 hover:border-[#D4AF37]/30 hover:text-[#D4AF37] transition-all duration-300 cursor-pointer"
              >
                <i className={`${social.icon} text-sm`} />
              </a>
            ))}
          </div>
        </div>
      </div>

    </section>
  );
}
